/* import __COLOCATED_TEMPLATE__ from './channel-limit-settings.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import type AppConversationCapacityLimit from 'embercom/models/app-conversation-capacity-limit';

interface Signature {
  Args: {
    model: AppConversationCapacityLimit;
  };
}

export default class ChannelLimitSettings extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;

  get model() {
    return this.args.model;
  }

  get channelLimitsEnabled() {
    return this.model.channelLimitsEnabled;
  }

  get emailLimit() {
    return this.model.channelLimits?.email ?? this.model.capacityLimit;
  }

  get messengerLimit() {
    return this.model.channelLimits?.messenger ?? this.model.capacityLimit;
  }

  get isValid() {
    return this.model.validations.isValid;
  }

  private normalizedChannelLimits(overrides: Record<string, number> = {}) {
    return {
      email: this.model.channelLimits?.email ?? this.model.capacityLimit,
      messenger: this.model.channelLimits?.messenger ?? this.model.capacityLimit,
      ...overrides,
    };
  }

  @action async toggleChannelLimitsEnabled() {
    this.model.channelLimitsEnabled = !this.model.channelLimitsEnabled;
    this.model.channelLimits = this.normalizedChannelLimits();

    await this.save();
  }

  @action updateEmailLimit(event: Event) {
    let value = parseInt((event.target as HTMLInputElement).value, 10);
    if (isNaN(value) || value < 0) {
      return;
    }
    this.model.channelLimits = this.normalizedChannelLimits({ email: value });
    this.debouncedSave();
  }

  @action updateMessengerLimit(event: Event) {
    let value = parseInt((event.target as HTMLInputElement).value, 10);
    if (isNaN(value) || value < 0) {
      return;
    }
    this.model.channelLimits = this.normalizedChannelLimits({ messenger: value });
    this.debouncedSave();
  }

  @action async validate() {
    this.model.errors.clear();

    let { validations } = await this.model.validations.validate();
    if (!validations.isValid) {
      validations.errors.forEach((error: any) => {
        this.model.errors.add(error.attribute, error.message);
      });
    }
  }

  @action async debouncedSave(debounceTime = 800) {
    await this.validate();
    if (this.isValid) {
      debounce(this, this.save, debounceTime);
    }
  }

  @action async save() {
    await this.validate();
    if (!this.isValid) {
      return;
    }

    try {
      await this.model.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.workload-management.update-success-notification'),
      );
    } catch (_: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('settings.workload-management.update-fail-notification'),
      );
      this.model.rollbackAttributes();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::WorkloadManagement::ChannelLimitSettings': typeof ChannelLimitSettings;
    'inbox/workload-management/channel-limit-settings': typeof ChannelLimitSettings;
  }
}
